import { Form } from '@/common/form/Form';
import { FormFieldGroupContainer } from '@/common/form/FormFieldGroupContainer';
import { useCloneAndRun } from '@/common/hooks/jobs/useCloneAndRun';
import { useModuleFromRoute } from '@/common/hooks/route/useModuleFromRoute';
import { useRsrc } from '@/common/rsrc/RsrcContext';
import { Button, FormControl, FormHelperText, InputLabel, MenuItem, Select, Stack, Typography } from '@mui/material';
import { useEffect, useMemo, useRef, useState } from 'react';
import { KrCard } from '../card/KrCard';
import { KrDialog } from '../dialog/Dialog';
import { UserSelect } from './UserSelect';

export const ApprRouteDialog = ({ data, closeDialog }) => {
  // Dialog
  const [title, setTitle] = useState();

  const [apprRoutes, setApprRoutes] = useState();
  const [apprGroupToUsers, setapprGroupToUsers] = useState();
  const [selectedApprRoute, setSelectedApprRoute] = useState('');
  const module = useModuleFromRoute();

  const rsrc = useRsrc();
  const cloneAndRun = useCloneAndRun();

  const formRef = useRef();

  useEffect(() => {
    const reqParam = {
      entityObjs: data.entityObjs,
      entity: data.entity,
    };
    rsrc.data('skdr', 'APPR_ROUTE_DATA_PLUGIN', reqParam, data.entity).then((apprRoutes) => {
      // apprRoutes = [];
      setApprRoutes(apprRoutes);

      if (apprRoutes.length === 0) {
        setTitle('Approve ' + data.entityDisplayName);
      } else {
        setTitle('Route ' + data.entityDisplayName + ' for Approval');
      }
      if (apprRoutes.length === 1) {
        setSelectedApprRoute(apprRoutes[0]);
      }
    });
    rsrc.data('admin', 'GROUP_TO_USERS_DATA_PLUGIN', reqParam, data.entity).then((apprGroupToUsers) => {
      setapprGroupToUsers(apprGroupToUsers);
    });
  }, []);

  const submit = () => {
    if (apprRoutes.length === 0) {
      const patchObjs = data.entityObjs.map((entityObj) => {
        return { id: entityObj.id, status: 'APPROVED' };
      });
      rsrc.save(module, data.entity, patchObjs).then(() => {
        closeDialog({ apprRoute: false });
      });
      return;
    }

    if (formRef.current.hasErrorState()) {
      formRef.current.touchAll();
      return;
    }

    const entityIds = data.entityObjs.map((entityObj) => entityObj.id);
    const jobIdToApprover = formRef.current.formValues;

    cloneAndRun(selectedApprRoute.id, entityIds, jobIdToApprover).then((clonedJob) => {
      closeDialog({ apprRoute: true });
    });
  };

  const buttons = useMemo(() => {
    let buttons = [
      <Button onClick={() => closeDialog()} color="secondary" variant="outlined">
        Cancel
      </Button>,
    ];
    if (apprRoutes) {
      buttons.push(
        <Button
          onClick={() => submit()}
          color="primary"
          variant="contained"
          disabled={apprRoutes.length > 0 && !selectedApprRoute}
        >
          {apprRoutes?.length === 0 ? 'Approve' : 'Submit'}
        </Button>,
      );
    }
    return buttons;
  }, [apprRoutes, selectedApprRoute]);

  return (
    apprRoutes &&
    apprGroupToUsers && (
      <KrDialog title={title} closeDialog={closeDialog} buttons={buttons}>
        {/* If there are no approval routes, the user will simply approve */}
        {apprRoutes.length === 0 && (
          <Typography>Are you sure you want to approve this {data.entityDisplayName}?</Typography>
        )}

        {/* If there are approval routes, the user will configure the approval route */}
        {apprRoutes.length > 1 && (
          <Stack gap={1}>
            <Typography variant="h6">Select Approval Route</Typography>
            <KrCard minHeight={0}>
              <FormFieldGroupContainer>
                <FormControl variant="filled" fullWidth sx={{ marginBottom: '1rem' }}>
                  <InputLabel>Approval Route</InputLabel>
                  <Select value={selectedApprRoute} onChange={(event) => setSelectedApprRoute(event.target.value)}>
                    {apprRoutes.map((apprRoute) => (
                      <MenuItem key={apprRoute.code} value={apprRoute}>
                        {apprRoute.name}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>
                    The Approval Route determines the approval flow of the {data.entityDisplayName}
                  </FormHelperText>
                </FormControl>
              </FormFieldGroupContainer>
            </KrCard>
          </Stack>
        )}

        {selectedApprRoute && (
          <Stack gap={1}>
            <Typography variant="h6" gutterBottom={false}>
              Configure Approvers for Each Step
            </Typography>
            <Form ref={formRef}>
              {selectedApprRoute.steps.map((step, index) => (
                <KrCard minHeight={0} title={'Step ' + (index + 1)} key={selectedApprRoute.code + index}>
                  <FormFieldGroupContainer>
                    {Object.entries(step.apprGroupToJobIds).map(([apprGroup, jobIds]) => (
                      <UserSelect
                        key={apprGroup + index}
                        stepNum={index}
                        apprGroup={apprGroup}
                        jobIds={jobIds}
                        apprGroupToUsers={apprGroupToUsers}
                      />
                    ))}
                  </FormFieldGroupContainer>
                </KrCard>
              ))}
            </Form>
          </Stack>
        )}
      </KrDialog>
    )
  );
};
